import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";

import BaseLink from "~/components/links/BaseLink";

import FaIcon from "~/components/FaIcon";
import Image from "~/components/Image";

import { link } from "~/styles/typographyStyles";

import transition from "~/utilities/transition";

import useStringTranslation from "~/hooks/translations/useStringTranslation";

export const Wrapper = styled.article`
    ${transition({}, "background-color")};
    position: relative;
    padding: 20px;
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    &:hover,
    &:focus {
        background-color: ${({ theme }) =>
            theme.colors.backgroundColor.section};
    }
`;

export const Img = styled(Image)`
    margin-bottom: 25px;
`;

export const Content = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

export const ArticleLink = styled(BaseLink)`
    color: currentColor;
    margin-bottom: auto;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
    }
`;

const ArticleDate = styled.div`
    font-weight: 400;
    margin-bottom: 10px;
`;

export const Title = styled.h3`
    font-size: 2.3rem;
    line-height: 1.43;
    margin-bottom: ${({ smallCard }) => (smallCard ? `0px` : `30px`)};
`;

export const Subtitle = styled.strong`
    font-weight: 900;
    font-size: 0.75em;
    margin-bottom: 10px;
`;

export const Footer = styled.div`
    border-bottom: ${({ theme, smallCard }) =>
        smallCard ? `none` : `1px solid ${theme.palette.borderGray}`};
    padding-bottom: 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: auto;
`;

export const ReadMore = styled.span`
    ${transition({}, "color")};
    ${link};
    color: ${({ theme }) => theme.colors.typography.links};
    margin-right: 10px;
    display: flex;
    align-items: center;

    ${Wrapper}:hover &,
    ${Wrapper}:focus & {
        color: ${({ theme }) => theme.colors.typography.linksHover};
    }
`;

export const ReadMoreIcon = styled(FaIcon)`
    margin-left: 10px;
`;

export const TypeLink = styled(BaseLink)`
    ${transition({}, "color")};
    position: relative;
    z-index: 2;
    font-size: 1.8rem;
    color: ${({ theme }) => theme.palette.grayOne};

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.typography.links};
    }
`;

const NewsCard = ({
    image = null,
    title,
    date,
    types = [],
    url,
    subtitle,
    ctaCopyOverride,
    urlOverride = "",
    openUrlOverrideInANewTab = false,
    smallCard = false,
}) => {
    const type = types.length ? types[0] : {};
    const link = urlOverride ? urlOverride : url;

    const { readMoreText } = useStringTranslation([
        { text: "Read More", handle: "readMoreText" },
    ]);

    return (
        <Wrapper {...{ smallCard }}>
            {image ? <Img {...image} /> : null}
            <Content>
                {date ? <ArticleDate>{date}</ArticleDate> : null}
                {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
                <ArticleLink
                    to={link}
                    external={!!urlOverride && openUrlOverrideInANewTab}
                >
                    <Title {...{ smallCard }}>{title}</Title>
                </ArticleLink>
                <Footer>
                    <ReadMore>
                        {ctaCopyOverride ? ctaCopyOverride : readMoreText}
                        <ReadMoreIcon name={"chevron-right"} />
                    </ReadMore>
                    {type.linkUrl ? (
                        <TypeLink to={type.linkUrl}>
                            <FaIcon name={type.taxonomyDetails?.iconClass} />
                        </TypeLink>
                    ) : null}
                </Footer>
            </Content>
        </Wrapper>
    );
};

NewsCard.propTypes = {
    image: PropTypes.shape({
        altText: PropTypes.string.isRequired,
        sourceUrl: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
    }),
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    date: PropTypes.string,
    url: PropTypes.string.isRequired,
    types: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
            taxonomyDetails: PropTypes.shape({
                iconClass: PropTypes.string,
            }),
        }),
    ),
    urlOverride: PropTypes.string,
    openUrlOverrideInANewTab: PropTypes.bool,
    smallCard: PropTypes.bool,
};

export default NewsCard;
